import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { images } from '../utils/images';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigationLinks = [
    { name: 'Home', id: 'home' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button onClick={handleLogoClick} className="flex items-center">
            <img 
              src={images.logo} 
              alt="Life Care & Wellness Logo" 
              className="h-16 md:h-20 w-auto cursor-pointer"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-12 lg:space-x-16">
            {navigationLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-base font-medium hover:opacity-80 transition-opacity"
                style={{ color: '#5e5e5e' }}
              >
                {link.name}
              </button>
            ))}
            
            <button
              onClick={() => navigate('/blogs')}
              className="text-base font-medium hover:opacity-80 transition-opacity"
              style={{ color: '#5e5e5e' }}
              data-testid="nav-blogs-link"
            >
              Blogs
            </button>

            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className="flex items-center text-base font-medium hover:opacity-80 transition-opacity"
                style={{ color: '#5e5e5e' }}
              >
                Services
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>
              
              {isServicesOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl py-2 z-50"
                  style={{ border: '1px solid #e5e7eb' }}
                >
                  <button
                    onClick={() => {
                      navigate('/services/support-coordination');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    NDIS Support Coordination
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/mental-health');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    Mental Health Support Services
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/disability-support');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    NDIS Disability Support
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/sles');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    School Leaver Employment Services (SLES)
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/counselling');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    Counselling On The NDIS
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/employment');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    Employment On The NDIS
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/recreation');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    Recreation With The NDIS
                  </button>
                  <button
                    onClick={() => {
                      navigate('/services/therapies');
                      setIsServicesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    Therapies On The NDIS
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/about')}
              className="text-base font-medium hover:opacity-80 transition-opacity"
              style={{ color: '#5e5e5e' }}
            >
              About
            </button>

            <button
              onClick={() => navigate('/what-is-ndis-support-provider')}
              className="text-base font-medium hover:opacity-80 transition-opacity"
              style={{ color: '#5e5e5e' }}
              data-testid="nav-support-coordinator-link"
            >
              What is an NDIS Support Provider?
            </button>

            <button
              onClick={() => navigate('/contact')}
              className="text-base font-medium hover:opacity-80 transition-opacity"
              style={{ color: '#5e5e5e' }}
              data-testid="nav-contact-link"
            >
              Contact Us
            </button>

            <button
              onClick={() => navigate('/make-a-referral')}
              className="text-base font-medium hover:opacity-80 transition-opacity"
              style={{ color: '#5e5e5e' }}
              data-testid="nav-referral-link"
            >
              Make a Referral
            </button>

            <button
              onClick={() => navigate('/get-on-ndis-free')}
              className="text-base font-semibold hover:opacity-80 transition-opacity"
              style={{ color: '#81b956' }}
              data-testid="nav-ndis-free-link"
            >
              Get on the NDIS Free
            </button>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:+611300404229">
              <Button 
                variant="outline" 
                size="sm"
                className="border-2 hover:bg-opacity-10 transition-colors"
                style={{ 
                  borderColor: '#81b956',
                  color: '#81b956'
                }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Us
              </Button>
            </a>
            <Button 
              onClick={() => navigate('/contact')}
              size="sm"
              className="text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#81b956' }}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" style={{ color: '#2d6142' }} />
            ) : (
              <Menu className="w-6 h-6" style={{ color: '#2d6142' }} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {navigationLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-left text-base font-medium hover:opacity-80 transition-opacity px-2"
                  style={{ color: '#5e5e5e' }}
                >
                  {link.name}
                </button>
              ))}
              
              {/* Services submenu for mobile */}
              <div className="px-2 py-2">
                <p 
                  className="text-base font-semibold mb-2"
                  style={{ color: '#587740' }}
                >
                  Services
                </p>
                <button
                  onClick={() => {
                    navigate('/services/support-coordination');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  NDIS Support Coordination
                </button>
                <button
                  onClick={() => {
                    navigate('/services/mental-health');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  Mental Health Support Services
                </button>
                <button
                  onClick={() => {
                    navigate('/services/disability-support');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  NDIS Disability Support
                </button>
                <button
                  onClick={() => {
                    navigate('/services/sles');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  School Leaver Employment Services (SLES)
                </button>
                <button
                  onClick={() => {
                    navigate('/services/counselling');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  Counselling On The NDIS
                </button>
                <button
                  onClick={() => {
                    navigate('/services/employment');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  Employment On The NDIS
                </button>
                <button
                  onClick={() => {
                    navigate('/services/recreation');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  Recreation With The NDIS
                </button>
                <button
                  onClick={() => {
                    navigate('/services/therapies');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-sm pl-4 py-2 w-full hover:opacity-80 transition-opacity"
                  style={{ color: '#5e5e5e' }}
                >
                  Therapies On The NDIS
                </button>
              </div>
              
              <button
                onClick={() => {
                  navigate('/about');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-base font-medium hover:opacity-80 transition-opacity px-2"
                style={{ color: '#5e5e5e' }}
              >
                About
              </button>
              <button
                onClick={() => {
                  navigate('/what-is-ndis-support-provider');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-base font-medium hover:opacity-80 transition-opacity px-2"
                style={{ color: '#5e5e5e' }}
              >
                What is an NDIS Support Provider??
              </button>
              <button
                onClick={() => {
                  navigate('/blogs');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-base font-medium hover:opacity-80 transition-opacity px-2"
                style={{ color: '#5e5e5e' }}
              >
                Blogs
              </button>

              <button
                onClick={() => {
                  navigate('/contact');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-base font-medium hover:opacity-80 transition-opacity px-2"
                style={{ color: '#5e5e5e' }}
              >
                Contact Us
              </button>
              <button
                onClick={() => {
                  navigate('/make-a-referral');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-base font-medium hover:opacity-80 transition-opacity px-2"
                style={{ color: '#5e5e5e' }}
              >
                Make a Referral
              </button>
              <button
                onClick={() => {
                  navigate('/get-on-ndis-free');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-base font-semibold hover:opacity-80 transition-opacity px-2"
                style={{ color: '#81b956' }}
              >
                Get on the NDIS Free
              </button>

              <div className="flex flex-col space-y-2 pt-4">
                <a href="tel:+611300404229" className="w-full">
                  <Button 
                    variant="outline" 
                    className="w-full border-2"
                    style={{ 
                      borderColor: '#81b956',
                      color: '#81b956'
                    }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Us
                  </Button>
                </a>
                <Button 
                  onClick={() => {
                    navigate('/contact');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-white"
                  style={{ backgroundColor: '#81b956' }}
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
